/**
 * BidCelebrationContext - Global state for BD celebration effects
 *
 * Allows triggering the celebration from anywhere in the app,
 * particularly useful when API responses indicate a submission was recorded.
 */

import { createContext, useContext, useState, useCallback, type ReactNode } from 'react';
import { BidCelebration } from '@/components/delight/BidCelebration';
import { useDelight } from '@/hooks/useDelight';

interface CelebrationState {
  todayCount: number;
  dailyTarget: number;
  isTargetMet: boolean;
}

interface BidCelebrationContextValue {
  /** Trigger the celebration with submission stats */
  celebrateSubmission: (todayCount: number, dailyTarget: number) => void;
  /** Trigger a win celebration */
  celebrateWin: (jobTitle?: string, winCount?: number) => void;
}

const BidCelebrationContext = createContext<BidCelebrationContextValue | null>(null);

export function BidCelebrationProvider({ children }: { children: ReactNode }) {
  const [showCelebration, setShowCelebration] = useState(false);
  const [celebrationState, setCelebrationState] = useState<CelebrationState>({
    todayCount: 0,
    dailyTarget: 5,
    isTargetMet: false,
  });

  const { celebrateBidSubmitted, celebrateBidWon, preferences } = useDelight();

  const celebrateSubmission = useCallback(
    (todayCount: number, dailyTarget: number) => {
      // Skip if celebrations are disabled
      if (!preferences.enableCelebrations || !preferences.celebrateBidSubmissions) {
        return;
      }

      const isTargetMet = todayCount >= dailyTarget;

      setCelebrationState({
        todayCount,
        dailyTarget,
        isTargetMet,
      });
      setShowCelebration(true);

      // Also trigger the toast celebration
      celebrateBidSubmitted(todayCount, dailyTarget);
    },
    [preferences, celebrateBidSubmitted]
  );

  const celebrateWin = useCallback(
    (jobTitle?: string, winCount?: number) => {
      if (!preferences.enableCelebrations || !preferences.celebrateBidSubmissions) {
        return;
      }

      celebrateBidWon(jobTitle, winCount);
    },
    [preferences, celebrateBidWon]
  );

  const handleCelebrationComplete = useCallback(() => {
    setShowCelebration(false);
  }, []);

  return (
    <BidCelebrationContext.Provider value={{ celebrateSubmission, celebrateWin }}>
      {children}
      <BidCelebration
        trigger={showCelebration}
        todayCount={celebrationState.todayCount}
        dailyTarget={celebrationState.dailyTarget}
        isTargetMet={celebrationState.isTargetMet}
        onComplete={handleCelebrationComplete}
      />
    </BidCelebrationContext.Provider>
  );
}

export function useBidCelebration() {
  const context = useContext(BidCelebrationContext);
  if (!context) {
    throw new Error('useBidCelebration must be used within a BidCelebrationProvider');
  }
  return context;
}
